import React from 'react';
import { Helmet } from 'react-helmet-async';

const CourseVibeCoding: React.FC = () => {
  const modules = [
    {
      title: "Foundation & Setup",
      description: "Copilot configuration, AI setup, and version control mastery",
      icon: "⚙️"
    },
    {
      title: "Frontend Development",
      description: "Responsive apps, dashboards, and mobile-first design through conversation",
      icon: "🎨"
    },
    {
      title: "Backend & Cloud",
      description: "AWS infrastructure, databases, and APIs built conversationally",
      icon: "☁️"
    },
    {
      title: "Production Deployment",
      description: "Professional hosting, custom domains, and enterprise optimization",
      icon: "🚀"
    },
    {
      title: "Advanced Optimization",
      description: "Performance tuning, automation, and debugging at scale",
      icon: "⚡"
    }
  ];



  return (
    <>
      <Helmet>
        <title>Vibe Coding for Absolute Beginners - GitHub Copilot Course | FutureXSkills</title>
        <meta 
          name="description" 
          content="Master vibe coding with GitHub Copilot Agent mode. Build, deploy, and optimize real production apps through natural conversation - no coding required. Zero to AI developer in hours."
        />
        <meta name="keywords" content="vibe coding, github copilot, ai development, conversational programming, no code, copilot agent mode, beginner coding" />
        <link rel="canonical" href="https://futurexskills.com/course-vibe-coding" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Vibe Coding for Absolute Beginners - GitHub Copilot Course" />
        <meta property="og:description" content="Transform from complete beginner to AI-powered developer. Build production apps through conversation with GitHub Copilot Agent mode." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://futurexskills.com/course-vibe-coding" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vibe Coding for Absolute Beginners - GitHub Copilot Course" />
        <meta name="twitter:description" content="Master conversational development with GitHub Copilot. Build anything you can imagine through natural conversation." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/60 via-indigo-100/40 to-purple-100/50" />
          
          {/* Soft animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/6 left-1/6 w-80 h-80 bg-gradient-to-r from-blue-200/30 to-indigo-200/30 rounded-full blur-3xl animate-pulse-slow" />
            <div className="absolute top-2/3 right-1/5 w-96 h-96 bg-gradient-to-r from-indigo-200/25 to-purple-200/25 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-2/3 w-72 h-72 bg-gradient-to-r from-purple-200/20 to-pink-200/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '3s' }} />
            <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-gradient-to-r from-cyan-200/25 to-blue-200/25 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '4s' }} />
            
            {/* Gentle floating particles */}
            <div className="absolute top-1/4 right-1/3 w-4 h-4 bg-blue-300/40 rounded-full animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3s' }} />
            <div className="absolute top-3/4 left-1/4 w-3 h-3 bg-indigo-300/40 rounded-full animate-bounce" style={{ animationDelay: '1.5s', animationDuration: '4s' }} />
            <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-purple-300/40 rounded-full animate-bounce" style={{ animationDelay: '2.5s', animationDuration: '2s' }} />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block bg-gradient-to-r from-indigo-600/10 to-purple-600/10 px-4 py-2 rounded-full mb-6 border border-indigo-200">
              <span className="text-indigo-700 font-semibold">🌟 Revolutionary AI Development</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight pb-2">
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Vibe Coding</span> for the Agentic World
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-8">
              Master GitHub Copilot Agent Mode & Conversational Development
            </h2>

            <div className="mb-8">
              <a
                href="https://www.udemy.com/course/vibe-coding-for-absolute-beginners-with-github-copilot/?referralCode=905C612B31CD1D32A6C9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-8 py-4 rounded-lg hover:from-yellow-400 hover:to-orange-400 transition-all transform hover:scale-105 shadow-lg"
              >
                <span className="mr-2">🎓</span>
                View Course on Udemy
                <span className="ml-2">→</span>
              </a>
            </div>

            <p className="text-xl md:text-2xl text-gray-800 max-w-4xl mx-auto mb-8 leading-relaxed font-medium">
              🚀 The future is here: <span className="text-indigo-600 font-bold">Build enterprise applications through natural conversation</span> - no syntax, no complexity, just pure AI-powered creation!
            </p>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 mb-6 font-medium">
                <strong className="text-gray-900">Transform from complete beginner to AI agent master in hours.</strong>
              </p>
              <p className="text-lg text-gray-600 mb-8">
                In the agentic world, developers don't write code - they orchestrate AI agents. Learn to command GitHub Copilot Agent mode like a conductor leading an orchestra, creating production-ready applications through intelligent conversation.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
              <div className="bg-white/60 backdrop-blur-lg border border-indigo-200 rounded-lg p-6 shadow-lg">
                <div className="text-3xl mb-3">🤖</div>
                <div className="text-gray-900 font-bold text-lg mb-2">Agent Orchestration</div>
                <div className="text-gray-600">Command AI agents like a symphony conductor</div>
              </div>
              <div className="bg-white/60 backdrop-blur-lg border border-indigo-200 rounded-lg p-6 shadow-lg">
                <div className="text-3xl mb-3">⚡</div>
                <div className="text-gray-900 font-bold text-lg mb-2">Zero to Production</div>
                <div className="text-gray-600">Deploy enterprise apps in hours, not months</div>
              </div>
              <div className="bg-white/60 backdrop-blur-lg border border-indigo-200 rounded-lg p-6 shadow-lg">
                <div className="text-3xl mb-3">🌐</div>
                <div className="text-gray-900 font-bold text-lg mb-2">Agentic Future</div>
                <div className="text-gray-600">Master the skills that define the next decade</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="https://www.udemy.com/course/vibe-coding-for-absolute-beginners-with-github-copilot/?referralCode=905C612B31CD1D32A6C9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold px-8 py-4 rounded-lg transition-all transform hover:scale-105 shadow-lg text-lg"
              >
                ✨ Master Agentic Development
                <span className="ml-2">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* What You'll Build Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
              Real-World Agentic Applications You'll Create
            </h2>
            <p className="text-center text-gray-200 mb-12 max-w-4xl mx-auto text-lg font-medium">
              Build production-grade applications that showcase the power of agentic development. Each project demonstrates 
              advanced AI orchestration, real-time data processing, and enterprise-scale deployment - all through conversation.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mb-4">
                  <span className="text-3xl">📊</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Intelligent Data Dashboard</h3>
                <p className="text-gray-200 mb-4">
                  Build a self-updating analytics platform that processes live market data, generates insights, 
                  and adapts its interface based on user behavior patterns.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-cyan-400">▸</span>
                    <span className="text-gray-300 text-sm">Live data processing</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-teal-400">▸</span>
                    <span className="text-gray-300 text-sm">AI-generated insights</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
                <div className="w-16 h-16 bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mb-4">
                  <span className="text-3xl">🚀</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Auto-Scaling Cloud Infrastructure</h3>
                <p className="text-gray-200 mb-4">
                  Deploy applications that automatically optimize themselves - scaling resources, 
                  improving performance, and reducing costs through intelligent automation.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-emerald-400">▸</span>
                    <span className="text-gray-300 text-sm">AWS auto-optimization</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">▸</span>
                    <span className="text-gray-300 text-sm">Cost-intelligent scaling</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Perfect For Section */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-2xl p-8 mb-16">
              <h3 className="text-2xl font-bold text-center text-white mb-8">Perfect For</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-emerald-400">✨</span>
                    <span className="text-gray-300">Complete beginners - no coding background needed</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-cyan-400">🔄</span>
                    <span className="text-gray-300">Career changers entering tech through AI</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-emerald-400">💡</span>
                    <span className="text-gray-300">Entrepreneurs building MVPs fast and affordable</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-cyan-400">🚀</span>
                    <span className="text-gray-300">Anyone curious about conversational development</span>
                  </div>
                </div>
              </div>
              <div className="text-center mt-6">
                <p className="text-xl font-semibold text-white">
                  If you can describe what you want, you can build it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Comprehensive Curriculum */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
              Complete Agentic Development Mastery
            </h2>
            <p className="text-center text-gray-200 mb-12 max-w-4xl mx-auto text-lg">
              From AI agent basics to enterprise deployment - everything you need to become an agentic developer
            </p>

            <div className="space-y-6">
              {modules.map((module, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center text-3xl flex-shrink-0 border border-white/10">
                      {module.icon}
                    </div>
                    <div className="flex-1">
                      <div className="mb-3">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">{module.title}</h3>
                      </div>
                      <p className="text-gray-200 text-lg leading-relaxed mb-4">{module.description}</p>
                      
                      {/* Add detailed learning outcomes for each module */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {index === 0 && (
                          <>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                              <span className="text-gray-300">GitHub Copilot Agent setup & configuration</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                              <span className="text-gray-300">AI agent communication protocols</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                              <span className="text-gray-300">Version control for agentic workflows</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                              <span className="text-gray-300">Environment optimization for AI development</span>
                            </div>
                          </>
                        )}
                        {index === 1 && (
                          <>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                              <span className="text-gray-300">Conversational UI/UX design</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-teal-400 rounded-full"></span>
                              <span className="text-gray-300">Responsive layouts through prompts</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                              <span className="text-gray-300">Interactive dashboard creation</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-teal-400 rounded-full"></span>
                              <span className="text-gray-300">Mobile-first agentic development</span>
                            </div>
                          </>
                        )}
                        {index === 2 && (
                          <>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                              <span className="text-gray-300">AWS infrastructure through conversation</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                              <span className="text-gray-300">Database design & optimization</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                              <span className="text-gray-300">API creation & management</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                              <span className="text-gray-300">Serverless architecture patterns</span>
                            </div>
                          </>
                        )}
                        {index === 3 && (
                          <>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                              <span className="text-gray-300">CI/CD pipeline automation</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                              <span className="text-gray-300">Domain & SSL configuration</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                              <span className="text-gray-300">Production monitoring & alerts</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                              <span className="text-gray-300">Enterprise security implementation</span>
                            </div>
                          </>
                        )}
                        {index === 4 && (
                          <>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                              <span className="text-gray-300">Performance optimization strategies</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                              <span className="text-gray-300">Automated testing & debugging</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                              <span className="text-gray-300">Intelligent system automation</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                              <span className="text-gray-300">Advanced AI workflow orchestration</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl p-8 border border-white/10">
              <p className="text-xl text-white font-bold mb-2">
                Master the Agentic Development Stack:
              </p>
              <p className="text-lg text-gray-200">
                Web apps • Data platforms • Cloud infrastructure • AI systems • Automation tools
              </p>
            </div>
          </div>
        </section>

        {/* Architecting with Prompts Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
              Architecting with Prompts
            </h2>
            <p className="text-center text-gray-200 mb-12 max-w-4xl mx-auto text-lg">
              Learn to design enterprise-grade system architecture through conversational AI - no traditional diagrams or complex documentation needed
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-3xl">🏛️</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">System Architecture Design</h3>
                <p className="text-gray-200 mb-6">
                  Design complex system architectures by describing your requirements in natural language. 
                  AI agents translate your vision into scalable, production-ready infrastructure patterns.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                    <span className="text-gray-300">Microservices architecture through conversation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                    <span className="text-gray-300">Database schema design with AI guidance</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                    <span className="text-gray-300">API gateway configuration via prompts</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                    <span className="text-gray-300">Load balancing & scaling strategies</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-3xl">🔄</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Infrastructure Orchestration</h3>
                <p className="text-gray-200 mb-6">
                  Orchestrate complex cloud infrastructure using conversational commands. 
                  From networking to security, design enterprise systems through intelligent dialogue.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    <span className="text-gray-300">VPC & network design through prompts</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                    <span className="text-gray-300">Security groups & IAM policy creation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    <span className="text-gray-300">Multi-region deployment strategies</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                    <span className="text-gray-300">Disaster recovery & backup automation</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Performance Architecture</h3>
                <p className="text-gray-200 mb-6">
                  Design high-performance systems optimized for speed, scalability, and cost-efficiency. 
                  AI agents help architect solutions that handle millions of users effortlessly.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                    <span className="text-gray-300">Caching strategies & CDN configuration</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-pink-400 rounded-full"></span>
                    <span className="text-gray-300">Auto-scaling policies through conversation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                    <span className="text-gray-300">Database optimization & indexing</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-pink-400 rounded-full"></span>
                    <span className="text-gray-300">Real-time monitoring & alerting systems</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-3xl">🛡️</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Security Architecture</h3>
                <p className="text-gray-200 mb-6">
                  Implement enterprise-grade security through conversational design. 
                  From encryption to compliance, build secure systems using natural language specifications.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                    <span className="text-gray-300">Zero-trust security model implementation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    <span className="text-gray-300">Encryption & key management systems</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                    <span className="text-gray-300">Compliance automation (SOC2, GDPR)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    <span className="text-gray-300">Vulnerability scanning & threat detection</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-12 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl p-8 border border-white/10">
              <p className="text-xl text-white font-bold mb-2">
                🏗️ Architecture Through Conversation
              </p>
              <p className="text-lg text-gray-200">
                Design enterprise systems by describing your vision - AI agents handle the complex implementation details
              </p>
            </div>
          </div>
        </section>

        {/* The Vibe Coding Revolution */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              The Vibe Coding Revolution
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-red-600 mb-4">Traditional Path</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-red-500">❌</span>
                    <span className="text-gray-700">Months learning syntax</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-red-500">❌</span>
                    <span className="text-gray-700">Complex setup processes</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-red-500">❌</span>
                    <span className="text-gray-700">Manual everything</span>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-emerald-600 mb-4">Vibe Coding</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-emerald-500">✅</span>
                    <span className="text-gray-700">Describe ideas naturally</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-emerald-500">✅</span>
                    <span className="text-gray-700">AI builds automatically</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-emerald-500">✅</span>
                    <span className="text-gray-700">Deploy immediately</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-xl font-bold text-white">
                Enterprise results with individual effort.
              </p>
            </div>
          </div>
        </section>

        {/* Career Transformation Outcomes */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
              Your Career Transformation
            </h2>
            <p className="text-center text-gray-200 mb-12 max-w-4xl mx-auto text-lg font-medium">
              From complete beginner to agentic developer - master the skills that define the future of software development
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {[
                {
                  title: "AI Agent Orchestration Mastery",
                  description: "Command multiple AI agents like a symphony conductor, creating complex systems through intelligent delegation and coordination",
                  icon: "🎯",
                  color: "from-indigo-500 to-purple-500"
                },
                {
                  title: "Conversational Architecture Skills", 
                  description: "Design and deploy enterprise-grade applications using natural language - no code syntax barriers ever again",
                  icon: "🏗️",
                  color: "from-cyan-500 to-teal-500"
                },
                {
                  title: "Production Deployment Confidence",
                  description: "Launch applications to AWS cloud infrastructure with automated scaling, monitoring, and optimization",
                  icon: "🚀",
                  color: "from-emerald-500 to-green-500"
                },
                {
                  title: "Enterprise Architecture Thinking",
                  description: "Optimize like a senior architect - performance, cost, security, and scale through AI-powered insights",
                  icon: "⚡",
                  color: "from-orange-500 to-red-500"
                },
                {
                  title: "Advanced Debugging & Optimization",
                  description: "Debug across entire tech stacks using AI collaboration, solving complex problems through conversation",
                  icon: "🔧",
                  color: "from-purple-500 to-pink-500"
                },
                {
                  title: "Automated Systems Creation",
                  description: "Build self-managing, intelligent systems that adapt, scale, and optimize themselves autonomously",
                  icon: "🤖",
                  color: "from-blue-500 to-indigo-500"
                }
              ].map((outcome, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
                  <div className={`w-12 h-12 bg-gradient-to-r ${outcome.color} rounded-lg flex items-center justify-center mb-4`}>
                    <span className="text-2xl">{outcome.icon}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{outcome.title}</h3>
                  <p className="text-gray-200 leading-relaxed">{outcome.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl p-8 border border-white/10">
              <div className="max-w-3xl mx-auto">
                <p className="text-2xl font-bold text-white mb-4">
                  🌟 The Agentic Advantage
                </p>
                <p className="text-xl text-gray-200 mb-6">
                  While others learn syntax, you'll master <span className="text-indigo-300 font-bold">agent orchestration</span>. 
                  While others debug code, you'll <span className="text-purple-300 font-bold">command AI systems</span>.
                </p>
                <p className="text-lg text-gray-300 mb-4">
                  Once you master agentic development, there are no limits to what you can build. 
                  Any idea you can describe becomes a buildable, deployable reality.
                </p>
                <div className="inline-block bg-gradient-to-r from-indigo-500/30 to-purple-500/30 px-6 py-3 rounded-full border border-white/20">
                  <span className="text-white font-bold">Real skills for the AI-powered future</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl p-12 border border-white/10">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Start Building Today
              </h2>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 font-medium">
                Join the <span className="text-indigo-300 font-bold">vibe coding revolution</span>. The future of development is conversational.
              </p>
              
              <div className="bg-white/5 backdrop-blur-lg border border-white/20 rounded-2xl p-8 mb-8">
                <h3 className="text-3xl font-bold text-white mb-6">
                  Ready to Transform Your Ideas into Reality?
                </h3>
                <p className="text-xl text-gray-200 mb-6 font-medium">
                  Enroll now - <span className="text-indigo-300 font-bold">build your first app within the hour</span>.
                </p>
                
                <div className="bg-gradient-to-r from-indigo-500/30 to-purple-500/30 rounded-xl p-6 mb-8 border border-white/20">
                  <p className="text-2xl font-bold text-white mb-2">
                    ✨ The Agentic Promise ✨
                  </p>
                  <p className="text-xl text-gray-100 font-semibold">
                    Describe it. Build it. Deploy it. That simple.
                  </p>
                </div>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-center space-x-6 text-gray-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">🎯</span>
                      <span className="font-medium">Zero Code Required</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">⚡</span>
                      <span className="font-medium">Production Ready</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">🚀</span>
                      <span className="font-medium">Enterprise Scale</span>
                    </div>
                  </div>
                </div>
                
                <a
                  href="https://www.udemy.com/course/vibe-coding-for-absolute-beginners-with-github-copilot/?referralCode=905C612B31CD1D32A6C9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold px-10 py-5 rounded-xl transition-all transform hover:scale-105 shadow-xl text-xl"
                >
                  ✨ Master Agentic Development Now
                  <span className="ml-3 text-2xl">→</span>
                </a>
              </div>
              
              <p className="text-gray-300 text-lg">
                Join thousands of developers already building the future through conversation
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CourseVibeCoding;